// Currency utility for pricing localization using Paddle's currency detection

// Eurozone countries
const EUROZONE_COUNTRIES = [
  'AT',
  'BE',
  'CY',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PT',
  'SK',
  'SI',
  'ES',
]

// Get user's currency using Paddle's API
async function getUserCurrency() {
  // Check if we have cached data (valid for 1 hour)
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const cached = localStorage.getItem('_paddlePricesCache')
      if (cached) {
        const { data, timestamp } = JSON.parse(cached)
        const oneHour = 60 * 60 * 1000
        if (Date.now() - timestamp < oneHour) {
          window._paddlePrices = data
          return data.currency || 'USD'
        }
      }
    } catch (e) {
      // Cache read failed, continue to fetch
    }
  }

  try {
    // Use Paddle's pricing API for currency detection
    const response = await fetch('/api/get-paddle-currency')
    if (response.ok) {
      const data = await response.json()
      // Store the actual prices for later use
      window._paddlePrices = data

      // Cache the data for future use
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.setItem(
            '_paddlePricesCache',
            JSON.stringify({ data, timestamp: Date.now() }),
          )
        } catch (e) {
          // Cache write failed, not critical
        }
      }

      return data.currency || 'USD'
    }
  } catch (error) {
    console.log('Paddle currency detection failed, using fallback')
  }

  // Fallback to timezone detection only (no external API calls)
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const timezoneParts = timezone.split('/')
      if (timezoneParts.length > 1) {
        const region = timezoneParts[1]
        const timezoneToCountry = {
          London: 'GB',
          Amsterdam: 'NL',
          Brussels: 'BE',
          Paris: 'FR',
          Berlin: 'DE',
          Rome: 'IT',
          Madrid: 'ES',
          Athens: 'GR',
          Dublin: 'IE',
          Lisbon: 'PT',
          Helsinki: 'FI',
          Vienna: 'AT',
          Luxembourg: 'LU',
          Valletta: 'MT',
          Bratislava: 'SK',
          Ljubljana: 'SI',
          Tallinn: 'EE',
          Riga: 'LV',
          Vilnius: 'LT',
          Nicosia: 'CY',
          New_York: 'US',
          Los_Angeles: 'US',
          Chicago: 'US',
          Denver: 'US',
          Phoenix: 'US',
          Anchorage: 'US',
          Honolulu: 'US',
        }

        if (timezoneToCountry[region]) {
          return mapCountryToCurrency(timezoneToCountry[region])
        }
      }
    } catch (error) {
      console.log('Timezone detection failed')
    }
  }

  return 'USD' // Default to USD
}

// Map country code to currency
function mapCountryToCurrency(country) {
  if (country === 'GB') {
    return 'GBP'
  } else if (EUROZONE_COUNTRIES.includes(country)) {
    return 'EUR'
  } else {
    return 'USD'
  }
}

// Determine currency info based on currency code (async version)
export async function getCurrencyInfo() {
  const currency = await getUserCurrency()

  // Check if we have actual prices from Paddle
  if (
    typeof window !== 'undefined' &&
    window._paddlePrices &&
    window._paddlePrices.success
  ) {
    const paddleData = window._paddlePrices
    return {
      currency: paddleData.currency,
      symbol: getCurrencySymbol(paddleData.currency),
      oncePrice: paddleData.lifetimePrice,
      annualPrice: paddleData.subscriptionPrice,
    }
  }

  // Fallback to hardcoded prices based on detected currency
  if (currency === 'GBP') {
    return {
      currency: 'GBP',
      symbol: '£',
      oncePrice: '65',
      annualPrice: '25',
    }
  } else if (currency === 'EUR') {
    return {
      currency: 'EUR',
      symbol: '€',
      oncePrice: '65',
      annualPrice: '25',
    }
  } else {
    // Default to USD for all other currencies
    return {
      currency: 'USD',
      symbol: '$',
      oncePrice: '75',
      annualPrice: '25',
    }
  }
}

// Helper function to get currency symbol
function getCurrencySymbol(currency) {
  switch (currency) {
    case 'GBP':
      return '£'
    case 'EUR':
      return '€'
    case 'USD':
    default:
      return '$'
  }
}

// Get formatted price string (async version)
export async function getPriceString(priceType) {
  const { symbol, oncePrice, annualPrice } = await getCurrencyInfo()

  if (priceType === 'once') {
    return `${symbol}${oncePrice}`
  } else if (priceType === 'annual') {
    return `${symbol}${annualPrice}`
  }

  return `${symbol}${oncePrice}`
}

// Synchronous version for immediate use (fallback to USD)
export function getCurrencyInfoSync() {
  // Check if we have cached data from previous session
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const cached = localStorage.getItem('_paddlePricesCache')
      if (cached) {
        const { data, timestamp } = JSON.parse(cached)
        const oneHour = 60 * 60 * 1000
        if (Date.now() - timestamp < oneHour && data.success) {
          return {
            currency: data.currency,
            symbol: getCurrencySymbol(data.currency),
            oncePrice: data.lifetimePrice,
            annualPrice: data.subscriptionPrice,
          }
        }
      }
    } catch (e) {
      // Cache read failed, use default
    }
  }

  return {
    currency: 'USD',
    symbol: '$',
    oncePrice: '75',
    annualPrice: '25',
  }
}

// Synchronous version for immediate use (fallback to USD)
export function getPriceStringSync(priceType) {
  const { symbol, oncePrice, annualPrice } = getCurrencyInfoSync()

  if (priceType === 'once') {
    return `${symbol}${oncePrice}`
  } else if (priceType === 'annual') {
    return `${symbol}${annualPrice}`
  }

  return `${symbol}${oncePrice}`
}
